import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { config } from '../../config';
import './BillingDashboard.css';

interface BillingStatsProps {
  dateRange?: 'month' | 'quarter' | 'year' | 'all';
  onDateRangeChange?: (range: 'month' | 'quarter' | 'year' | 'all') => void;
}

interface Stats {
  totalRevenue: number;
  outstandingInvoices: number;
  outstandingAmount: number;
  overdueInvoices: number;
  overdueAmount: number;
  totalPayments: number;
  paymentsThisMonth: number;
}

const BillingStats: React.FC<BillingStatsProps> = ({
  dateRange = 'month',
  onDateRangeChange
}) => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, [dateRange]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { getValidToken } = await import('../../utils/tokenRefresh');
      
      // Get fresh token for each request to ensure we don't use stale tokens
      const getHeaders = async () => {
        const token = await getValidToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
      };
      
      // TODO: Implement API endpoint for billing stats
      // For now, calculate from invoices and payments
      // Get fresh token for each request to avoid race conditions
      const [invoicesResponse, paymentsResponse, overdueResponse] = await Promise.all([
        getHeaders().then(headers => axios.get(`${config.BACKEND_URL}/api/billing/invoices`, { headers })),
        getHeaders().then(headers => axios.get(`${config.BACKEND_URL}/api/billing/payments`, { headers })),
        getHeaders().then(headers => axios.get(`${config.BACKEND_URL}/api/billing/invoices/overdue`, { headers }))
      ]);
      
      const invoices = invoicesResponse.data || [];
      const payments = paymentsResponse.data || [];
      const overdueInvoices = overdueResponse.data || [];
      
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      const paymentsThisMonth = payments.filter((p: any) => {
        const paymentDate = new Date(p.payment_date);
        return paymentDate >= startOfMonth;
      });
      
      const totalRevenue = payments.reduce((sum: number, p: any) => sum + parseFloat(p.amount), 0);
      const paymentsThisMonthTotal = paymentsThisMonth.reduce((sum: number, p: any) => sum + parseFloat(p.amount), 0);
      
      const outstandingInvoices = invoices.filter((inv: any) => 
        inv.status !== 'paid' && inv.status !== 'cancelled'
      );
      const outstandingAmount = outstandingInvoices.reduce((sum: number, inv: any) => 
        sum + parseFloat(inv.balance_due), 0
      );
      
      const overdueAmount = overdueInvoices.reduce((sum: number, inv: any) => 
        sum + parseFloat(inv.balance_due), 0
      );
      
      setStats({
        totalRevenue,
        outstandingInvoices: outstandingInvoices.length,
        outstandingAmount,
        overdueInvoices: overdueInvoices.length,
        overdueAmount,
        totalPayments: payments.length,
        paymentsThisMonth: paymentsThisMonthTotal
      });
    } catch (err: any) {
      console.error('Error fetching billing stats:', err);
      setError(err.response?.data?.detail || 'Failed to load billing statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading statistics...</div>;
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="billing-stats-container">
      <div className="billing-stats-header">
        <h3>Billing Overview</h3>
        {onDateRangeChange && (
          <select
            value={dateRange}
            onChange={(e) => onDateRangeChange(e.target.value as 'month' | 'quarter' | 'year' | 'all')}
            className="date-range-select"
          >
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
            <option value="all">All Time</option>
          </select>
        )}
      </div>
      
      <div className="stats-grid">
        <div className="stat-card revenue-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>${stats.totalRevenue.toFixed(2)}</h3>
            <p>Total Revenue</p>
          </div>
        </div>
        
        <div className="stat-card payments-card">
          <div className="stat-icon">💳</div>
          <div className="stat-content">
            <h3>${stats.paymentsThisMonth.toFixed(2)}</h3>
            <p>Payments This Month</p>
          </div>
        </div>
        
        <div className="stat-card outstanding-card">
          <div className="stat-icon">📄</div>
          <div className="stat-content">
            <h3>{stats.outstandingInvoices}</h3>
            <p>Outstanding Invoices</p>
            <p className="stat-subtext">${stats.outstandingAmount.toFixed(2)}</p>
          </div>
        </div>
        
        <div className="stat-card overdue-card">
          <div className="stat-icon">⚠️</div>
          <div className="stat-content">
            <h3>{stats.overdueInvoices}</h3>
            <p>Overdue Invoices</p>
            <p className="stat-subtext">${stats.overdueAmount.toFixed(2)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingStats;

